import { useMutation } from '@apollo/client';
import {
  checkDynamicQuery,
  getDefaultValue
} from '@nile/client_management/src/Pebbles/ContractTemplatesContainer';
import {
  ALL_TABLES,
  EventEmitter,
  getAllColumnWidth,
  getColumnWidth,
  getTranslatedText,
  toast,
  useSuperListHook
} from '@nile/components';
import SuperList from '@nile/components/src/Components/SuperList/SuperList';
import {
  AccountClient,
  CONVERT_UNKNOWN_EMAIL_TO_CLIENT_CONVERSATION,
  CONVERT_UNKNOWN_EMAIL_TO_TICKET,
  DELETE_UNKNOWN_EMAILS,
  GET_UNKNOWN_EMAILS
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import {
  ActionCell,
  HTMLCell,
  TextCell
} from '@nile/mangifera/src/Components/Mail';
import { DateTimeCell } from '@nile/mangifera/src/Components/Task';
import { defaultColumns } from '@nile/mangifera/src/Constants/Tickets';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import {
  getAccessor,
  getType,
  isEditable,
  isFilterable,
  isSortable
} from '@nile/mangifera/src/Pebbles/MailContainer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

function TaskViewContainer(props) {
  const { intl } = props;
  const context = useContext(TicketContext);
  const flags = useFlags();
  const dynamicPaginationFlag = flags?.dynamicPaginationUnknownEmailTickets;
  const tableName = ALL_TABLES.unknownEmailTickets;
  const [listLoading, setListLoading] = useState(true);

  const viewId = props.activeView.id || tableName;
  const columnWidthObject = getAllColumnWidth(tableName);

  const {
    records,
    setRecords,
    tableCondition,
    updateTableFilterCondition
  } = useSuperListHook({
    viewId,
    tableName
  });

  const [data, setData] = useState([]);
  const [columnGroup, setColumnGroup] = useState(null);

  const DEFAULT_SORT = [
    {
      attribute: 'receivedTime',
      order: 'DESC'
    }
  ];

  const DEFAULT_CONDITION = {
    joinOperator: 'AND',
    operands: [
      {
        operator: 'is',
        value: 'CLIENT',
        attribute: 'module'
      }
    ]
  };

  const [convertUnknownEmailToClientConversation] = useMutation(
    CONVERT_UNKNOWN_EMAIL_TO_CLIENT_CONVERSATION,
    {
      client: AccountClient
    }
  );

  const [convertUnknownEmailsToTicket] = useMutation(
    CONVERT_UNKNOWN_EMAIL_TO_TICKET
  );

  const [deleteUnknownEmails] = useMutation(DELETE_UNKNOWN_EMAILS, {
    client: AccountClient
  });

  defaultColumns.subject.actions = {
    onClick: context.openTicket
  };

  const personalizedColumns = [
    {
      name: 'fromAddress',
      label: getTranslatedText(intl, 'admin.EmailServerSettings.h2.outgo.from'),
      dataType: 'string',
      uiDataType: 'text',
      filterType: 'text'
    },
    {
      name: 'subject',
      label: getTranslatedText(intl, 'admin.alertTicketTemplate.subject'),
      dataType: 'string',
      uiDataType: 'text',
      filterType: 'text'
    },
    {
      name: 'receivedTime',
      label: getTranslatedText(intl, 'unknownEmail.receivedTime'),
      dataType: 'daterange'
    }
  ];

  const getActions = (col) => {
    switch (col.name) {
      case 'title':
        return {};
      case 'action':
      case 'subject':
        return {
          onClickConvertClientConversation: (obj, selectedClient) => {
            convertUnknownEmailToClientConversation({
              variables: {
                clientId: selectedClient?.accountId,
                unknownEmailId: obj?.unknownEmailId
              }
            }).then((res) => {
              fetchViewData();
              context.openTab({
                module: 'CLIENT',
                data: {
                  id: selectedClient?.accountId,
                  name: selectedClient?.name,
                  value: selectedClient?.name
                }
              });
            });
          },
          onClickConvertToTicket: (obj, selectedClient, requester) => {
            convertUnknownEmailsToTicket({
              variables: {
                clientId: selectedClient?.accountId,
                unknownEmailIds: obj?.unknownEmailId,
                requesterId: requester?.userId
              }
            }).then((res) => {
              fetchViewData();
              toast(
                'success',
                'Ticket moved under the chosen client successfully'
              );
            });
          },
          onDelete: (obj, callback) => {
            deleteUnknownEmails({
              variables: { unknownEmailIds: [obj?.unknownEmailId] }
            }).then((res) => {
              fetchViewData();
              if (callback) {
                callback();
              }
              EventEmitter.dispatch('refreshMailViewCount', [
                props.match.params.id
              ]);
            });
          },
          onClick: (meta) => {
            //
          }
        };

      default:
        break;
    }
  };

  const fetchViewData = async () => {
    setListLoading(true);

    const cols = personalizedColumns;
    const allColumns = cols.map((col) => {
      return {
        Header: col.label,
        header: col.label,
        accessor: getAccessor(col),
        accessorKey: getAccessor(col),
        type: getType(col),
        disableResizing: false,
        className: 'sticky',
        editable: isEditable(col),
        actions: getActions(col),
        disableSortBy: !isSortable(col),
        disableFilters: !isFilterable(col),
        helpers: {
          getDefaultValue,
          updateConditions: updateTableFilterCondition,
          checkDynamicQuery
        },
        size: getColumnWidth(columnWidthObject, col.name),
        field: col
      };
    });
    allColumns.push({
      Header: ' ',
      header: ' ',
      accessor: 'actions',
      accessorKey: 'actions',
      // width: 14,
      id: 'actions',
      pinned: 'right',
      type: 'Action',
      action: true,
      disableSortBy: true,
      disableFilters: true,
      actions: getActions({ name: 'action' }),
      field: { name: 'action' }
    });

    setColumnGroup(allColumns);
    setListLoading(false);
  };

  useEffect(() => {
    if (!columnGroup) {
      fetchViewData();
    }
  }, []);

  const defaultColumnCells = {
    HTML: HTMLCell,
    Text: TextCell,
    Action: ActionCell,
    DateTime: DateTimeCell
  };

  return (
    <div className="fhp w100">
      <SuperList
        noBulkSelect={false}
        records={records}
        setRecords={setRecords}
        columns={columnGroup}
        data={data}
        setData={setData}
        defaultColumnCells={defaultColumnCells}
        isLoading={listLoading}
        fullHeight
        dynamicRowCount={dynamicPaginationFlag}
        columnWidthPersonalization
        tableName={tableName}
        viewId={viewId}
        client={AccountClient}
        query={GET_UNKNOWN_EMAILS}
        accessorKey="getUnknownEmails"
        dataKey="unknownEmails"
        emptyState={{
          noDataImage: `url(${envConfig.imageRoot}svg/UnknownEmails.svg)`,
          heading: getTranslatedText(intl, 'alert.noMailsFound.heading'),
          description: getTranslatedText(intl, 'alert.noMailsFound.desc')
        }}
        defaultCondition={DEFAULT_CONDITION}
        defaultSort={DEFAULT_SORT}
        tableCondition={tableCondition}
        padded="sm"
      />
    </div>
  );
}

export default injectIntl(withRouter(TaskViewContainer));
